import {Injectable} from '@angular/core';

import {NodeColors} from 'shared/classes/node-colors';
import {AnswerOption} from 'shared/models/questions/answer-option';
import {Question} from 'shared/models/questions/question';
import {QuestionChain} from 'shared/models/questions/question-chain';
import {QuestionType} from 'shared/models/questions/question-type';
import {Tiplist} from 'shared/models/tips/tiplist';
import {QuestionTypeFactoryService} from 'shared/questions/services/question-type-factory.service';
import * as Edge from '../interfaces/edge';
import * as Node from '../interfaces/node';
import { Jump } from '../interfaces/jump';
import { IdType, Network } from 'vis-network/peer';

type NodeObj = Node.Node;
type EdgeObj = Edge.Edge;
type Nodes = Array<NodeObj>;
type Edges = Array<EdgeObj>;
type IDs = Array<IdType>;
interface TreeElements {
  nodes: Nodes;
  edges: Edges;
  nodeIds?: Set<IdType>;
}

@Injectable()
export class NetworkService {

  public rootNode: NodeObj;
  private usedQuestions: Array<Node.Data> = [];
  private lastSelectedNodeId: IdType = '';
  private nodeColors: NodeColors = new NodeColors();

  constructor(private questionTypeFactoryService: QuestionTypeFactoryService) { }

  public addFirstQuestion(question: Question, nodes: Node.Set, edges: Edge.Set, network: Network, allNodes: Node.Set, allEdges: Edge.Set): void {
    const newNode = this.createNodeObject(question._id, 0, question);
    nodes.add(newNode);
    allNodes.add(newNode);
    this.rootNode = nodes.get(question._id);
    this.addChildNodes(this.rootNode, nodes, edges, network, allNodes, allEdges);
  }

  public editNode(sourceNode: NodeObj, editNodeCallback: Node.Callback, nodes: Node.Set, edges: Edge.Set, network: Network, allNodes: Node.Set, allEdges: Edge.Set): void {
    editNodeCallback(sourceNode);
    allNodes.get(sourceNode.id).data = nodes.get(sourceNode.id).data;
    allNodes.get(sourceNode.id).title = nodes.get(sourceNode.id).title;

    if (Node.Tools.isQuestion) {
      this.addChildNodes(sourceNode, nodes, edges, network, allNodes, allEdges);
    }
  }

  public selectNode(params: Node.SelectEvent, nodes: Node.Set, edges: Edge.Set, network: Network, allNodes: Node.Set, allEdges: Edge.Set): void {
    const deselected: TreeElements = this.deselectNode(params, nodes, edges, network);
    console.log("ENTER select");

    this.lastSelectedNodeId = params.nodes[0];
    const nextEdgeIds: IDs = this.getEdgeIdsFromNode(this.lastSelectedNodeId, allEdges);

    function enable<T extends NodeObj | EdgeObj>(target: T): T {
      target.hidden = false;
      target.physics = true;
      return target;
    }

    const selectedEdges: Edges = allEdges.get(nextEdgeIds)
      .map(enable);

    const selectedNodes: Nodes = selectedEdges.map(e => allNodes.get(e.to))
      .map(enable);

    const selectedNode: NodeObj = allNodes.get(this.lastSelectedNodeId);
    selectedNodes.push(enable(selectedNode));

    console.log("UPDATE Nodes: "+deselected.nodes.length+ " " + selectedNodes.length);
    nodes.update(deselected.nodes.concat(selectedNodes));

    console.log("UPDATE Edges: "+deselected.edges.length+ " " + selectedEdges.length);
    edges.update(deselected.edges.concat(selectedEdges));

    console.log("START stab");
    network.stabilize();
    console.log("END stab");
  }

  public deselectNode(params: Node.SelectEvent, nodes: Node.Set, edges: Edge.Set, network: Network): TreeElements {
    if (!this.lastSelectedNodeId) {
      return {
        nodes: [],
        edges: [],
      };
    }

    const selectedPath: IDs = this.getNodePath(params.nodes[0], nodes, edges);
    const deselectedPath: IDs = this.getNodePath(this.lastSelectedNodeId, nodes, edges);

    let nodesIdsToHide: IDs = deselectedPath.filter((nodeId) => {
      return selectedPath.indexOf(nodeId) < 0;
    });

    const edgeIdsToHide: IDs = edges.getIds({
      filter: (edge: EdgeObj): boolean => {
        return nodesIdsToHide.indexOf(edge.from) >= 0;
      },
    });

    // the first item in the deselected Path is neighbour to the root node and should not be hidden
    nodesIdsToHide = nodesIdsToHide.concat(edgeIdsToHide).slice(1);

    function disable<T extends NodeObj | EdgeObj>(target: T): T {
      target.hidden = true;
      target.physics = false;
      return target;
    }

    const result: TreeElements = {
      nodes: nodes.get(nodesIdsToHide).map(disable),
      edges: edges.get(edgeIdsToHide).map(disable),
    };

    return result;
  }

  public deleteNode(nodeData: Node.SelectedElements, nodes: Node.Set, edges: Edge.Set, network: Network, allNodes: Node.Set, allEdges: Edge.Set): void {
    const idsToDelete: IDs = this.getSubtreeIds(nodeData.nodes[0], allNodes, allEdges);
    edges.remove(idsToDelete);
    nodes.remove(idsToDelete.concat(nodeData.nodes[0]));
    nodes.add(this.createNodeObject(<string> nodeData.nodes[0]));
    network.stabilize();
    allEdges.remove(idsToDelete);
    allNodes.remove(idsToDelete.concat(nodeData.nodes[0]));
    allNodes.add(this.createNodeObject(<string> nodeData.nodes[0]));
  }

  public getUnusedQuestions(targetNode: NodeObj, nodes: Node.Set, edges: Edge.Set, questions: Array<Question>): Array<Question> {
    const usedNodeIds: IDs = this.getNodePath(targetNode.id, nodes, edges)
      .filter((nodeId: IdType) => {
        return nodeId !== targetNode.id;
      });

    const usedQuestionIds: Array<string> = nodes.get(usedNodeIds).map((node: NodeObj) => {
      return (<Question> node.data)._id;
    });

    const unusedQuestions: Array<Question> = questions.filter((question: Question) => {
      return usedQuestionIds.indexOf(question._id) < 0;
    });

    return unusedQuestions;
  }

  public drawExistingTree(tree: Array<QuestionChain>, allNodes: Node.Set, allEdges: Edge.Set, network: Network, reallyAllNodes: Node.Set, reallyAllEdges: Edge.Set): void {
    let treeElements: TreeElements = {
      nodes: [],
      edges: [],
      nodeIds: new Set(),
    };

    const chainIds: Array<Array<string>> = tree.map((chain: QuestionChain) => {
      const rootId: Array<string> = [chain.questions[0]._id];
      const neighbourIds: Array<string> = chain.questions
        .filter((question: Question, index: number) => { return !chain.jump || index < chain.questions.length-1; })
        .map((question: Question, index: number) => {
          return this.generateChainId(chain, index + 1);
        });

      return rootId.concat(neighbourIds);
    });

    treeElements = this.addRootNode(tree, chainIds, treeElements);
    treeElements = this.addQuestionNodes(tree, chainIds, treeElements);
    treeElements = this.addTiplistNodes(tree, chainIds, treeElements);
    treeElements = this.addEmptyNodes(tree, chainIds, treeElements);

    console.log("Nodes: "+treeElements.nodes.length);
    console.log("Edges: "+treeElements.edges.length);
    allNodes.update(treeElements.nodes.filter(n => !n.hidden));
    allEdges.update(treeElements.edges.filter(e => !e.hidden));
    this.rootNode = allNodes.get(tree[0].questions[0]._id);

    console.log("START STABILIZE");
    network.stabilize();
    console.log("END STABILIZE");
    reallyAllNodes.update(treeElements.nodes);
    reallyAllEdges.update(treeElements.edges);
  }

  private addRootNode(tree: Array<QuestionChain>, chainIds: Array<Array<string>>, currentElements: TreeElements): TreeElements {
    currentElements.nodes.push(this.createNodeObject(tree[0].questions[0]._id, 0, tree[0].questions[0]));
    currentElements.nodeIds.add(tree[0].questions[0]._id);
    return currentElements;
  }

  private addQuestionNodes(tree: Array<QuestionChain>, chainIds: Array<Array<string>>, currentElements: TreeElements): TreeElements {
    for (let i: number = 0; i < tree.length; i++) {
      const chain: QuestionChain = tree[i];
      for (let j: number = 1; j < chain.questions.length; j++) {
        currentElements = this.addNode(currentElements, chainIds[i][j - 1], j, chain.questions[j - 1], chain.answers[j - 1], chain.questions[j], null, j == chain.questions.length -1 ? chain.jump : undefined);
      }
    }

    return currentElements;
  }

  private addTiplistNodes(tree: Array<QuestionChain>, chainIds: Array<Array<string>>, currentElements: TreeElements): TreeElements {
    for (let i: number = 0; i < tree.length; i++) {
      if (!tree[i].tiplist) {
        continue;
      }

      const chain: QuestionChain = tree[i];
      const j: number = chain.questions.length;
      currentElements = this.addNode(currentElements, chainIds[i][j - 1], j, chain.questions[j - 1], chain.answers[j - 1], null, chain.tiplist);
    }

    return currentElements;
  }

  private addEmptyNodes(tree: Array<QuestionChain>, chainIds: Array<Array<string>>, currentElements: TreeElements): TreeElements {
    for (let i: number = 0; i < tree.length; i++) {
      const chain: QuestionChain = tree[i];

      for (let j: number = 0; j < chain.questions.length; j++) {
        if ( chain.jump && j == chain.questions.length-1) {
          continue;
        }
        const possibleAnswers: Array<AnswerOption> = chain.questions[j].type.getAnswerOptions();

        // tslint:disable-next-line:prefer-for-of for-of-loops are slow, and this part needs to be fast
        for (let k: number = 0; k < possibleAnswers.length; k++) {
          currentElements = this.addNode(currentElements, chainIds[i][j], j + 1, chain.questions[j], possibleAnswers[k].value);
        }
      }
    }

    return currentElements;
  }

  private addNode(currentElements: TreeElements,
                  sourceId: string,
                  level: number,
                  sourceQuestion: Question,
                  answerValue: string,
                  nodeQuestion?: Question,
                  tiplist?: Tiplist,
                  jump?: Array<string>): TreeElements {

    const nodeId: string = `${sourceId}_${answerValue}`;
    if (currentElements.nodeIds.has(nodeId)) {
      return currentElements;
    }

    currentElements.nodes.push(this.createNodeObject(nodeId, level, nodeQuestion, tiplist, jump));

    const answer: AnswerOption = this.getAnswerOptionByValue(sourceQuestion, answerValue);
    currentElements.edges.push(this.createEdgeObject(sourceId, answer, level));
    currentElements.nodeIds.add(nodeId);
    return currentElements;
  }

  private generateChainId(chain: QuestionChain, index: number): string {
    if (index === 0) {
      return chain.questions[0]._id;
    }

    const answers: string = chain.answers.slice(0, index).join('_');
    return `${chain.questions[0]._id}_${answers}`;
  }

  private getAnswerOptionByValue(question: Question, answerValue: string): AnswerOption {
    return question.type.getAnswerOptions().find((option: AnswerOption) => {
      return option.value === answerValue;
    });
  }

  private getNodePath(nodeId: IdType, nodes: Node.Set, edges: Edge.Set): IDs {
    const nodeIds: IDs = [];

    while (nodeId && nodeId !== this.rootNode.id) {
      nodeIds.unshift(nodeId);

      const edge: EdgeObj = edges.get(nodeId);
      if (edge) {
        nodeId = edge.from;
      } else {
        break;
      }
    }

    nodeIds.unshift(this.rootNode.id);
    return nodeIds;
  }

  private addChildNodes(sourceNode: NodeObj, nodes: Node.Set, edges: Edge.Set, network: Network, allNodes: Node.Set, allEdges: Edge.Set): void {
    if (!Node.Tools.isQuestion(sourceNode.data)) {
      return;
    }

    const questionType: QuestionType = this.questionTypeFactoryService.createQuestionType(sourceNode.data.type.name, sourceNode.data.type);
    const newNodes: Nodes = [];
    const newEdges: Edges = [];
    questionType.getAnswerOptions().forEach((option: AnswerOption) => {
      newNodes.push(this.createNodeObject(`${sourceNode.id}_${option.value}`));
      newEdges.push(this.createEdgeObject(sourceNode.id, option));
    });

    nodes.add(newNodes);
    edges.add(newEdges);
    network.stabilize();
    allNodes.add(newNodes);
    allEdges.add(newEdges);
  }

  private getSubtreeIds(nodeId: IdType, nodes: Node.Set, edges: Edge.Set): IDs {
    const edgeIds: IDs = this.getEdgeIdsFromNode(nodeId, edges);

    const subtreeIds: IDs = edgeIds.reduce((workingArray: IDs, currentId: IdType) => {
        return workingArray.concat(this.getSubtreeIds(currentId, nodes, edges));
    },                                     []);

    return edgeIds.concat(subtreeIds);
  }

  private getEdgeIdsFromNode(nodeId: IdType, edges: Edge.Set): IDs {
    const nextEdgeIds: IDs = edges.getIds({
      filter: (edge: EdgeObj): boolean => {
        return edge.from === nodeId;
      },
    });

    return nextEdgeIds;
  }

  private getEdgeIdsToNode(nodeId: IdType, edges: Edge.Set): IDs {
    const nextEdgeIds: IDs = edges.getIds({
      filter: (edge: EdgeObj): boolean => {
        return edge.to === nodeId;
      },
    });

    return nextEdgeIds;
  }

  private createNodeObject(id: string = null, level: number = -1, question: Question = null, tiplist: Tiplist = null, jump: Array<string> = null): NodeObj {

    const properties: NodeObj = {
      id: id,
      label: '',
    };

    if (jump) {
      properties.label = question.title.de;
      let targetId = id.replace(/_.*/g, '');
      jump.forEach((answer: string) => {targetId += '_' + answer; });
      properties.data = new Jump(targetId, question);
    } else {
      if (question) {
        properties.label = question.title.de;
        properties.data = question;
      }

      if (tiplist) {
        properties.label = tiplist.title;
        properties.data = tiplist;
      }
    }

    if (level >= 0) {
      if (jump) {
        properties.color = {
          background: '#00B300',
          border: 'rgba(0, 102, 0, 1)',
          highlight: {
            background: 'rgba(0, 204, 0, 1)',
            border: 'rgba(51, 153, 51, 1)',
          },
        };
      } else if (question) {
        if (level < this.nodeColors.backgroundColors.length) {
          properties.color = this.nodeColors.backgroundColors[level];
        } else {
          properties.color = this.nodeColors.constantBackground;
        }

      }

      const visibleLayers: number = 1;
      properties.hidden = level > visibleLayers;
      properties.physics = level < visibleLayers + 1;
    }

    return properties;
  }

  private createEdgeObject(from: IdType, answer: AnswerOption, level: number = -1): EdgeObj {
    try {
      const properties: EdgeObj = {
        id: `${from}_${answer.value}`,
        label: answer.label,
        from: from,
        to: `${from}_${answer.value}`,
        answerValue: answer.value,
        smooth: false,
        hidden: level > 1,
        physics: level <= 1,
      };

      return properties;
    }
    catch (e) {
      console.error("Failed createEdgeObject at from: "+from+" "+e);
    }
    return {};
  }

}



// WEBPACK FOOTER //
// /usr/src/app/src/app/modules/dynamic-tips/tree-view/services/network.service.ts